import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FiSearch, FiRefreshCw, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";

const Access = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const hasFetched = useRef(false);

    // Fetch customers
    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers", {
                withCredentials: true,
            });

            // Ensure cancelFlag is always number
            const normalizedData = res.data.map((c) => ({
                ...c,
                cancelFlag: Number(c.cancelFlag),
            }));

            setCustomers(normalizedData);
        } catch (err) {
            toast.error("Failed to fetch customers");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!hasFetched.current) {
            fetchCustomers();
            hasFetched.current = true;
        }
    }, [fetchCustomers]);

    const handleUpdateCustomer = async (id, payload) => {
        try {
            // Force numeric payload before sending
            const updatedPayload = {
                ...payload,
                cancelFlag: Number(payload.cancelFlag),
            };

            await axios.put(
                `https://www.azzenterprises.com/api/customers/update/${id}`,
                updatedPayload,
                { withCredentials: true }
            );

            setCustomers((prev) =>
                prev.map((c) =>
                    c.customer_id === id ? { ...c, ...updatedPayload } : c
                )
            );

            const editedFields = Object.keys(updatedPayload).map((key) => {
                if (key === "cancelFlag") {
                    return `Privilege (${updatedPayload[key] === 1 ? "Enabled" : "Disabled"})`;
                }
                return `${key} (${updatedPayload[key]})`;
            });

            toast.info(`${editedFields.join(", ")} successfully!`);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to update user";
            toast.error(message);
        }
    };

    const columns = [
        { name: "#", selector: (row, index) => index + 1, width: "60px" },

        {
            name: "Customer Name",
            selector: (row) => row.full_name,
            sortable: true,
        },
        {
            name: "eGiro status",
            selector: (row) => row.requestType,
            sortable: true,
            cell: (row) => {
                switch (row.requestType) {
                    case "Creation":
                        return <Badge bg="success">Active</Badge>;
                    case "Cancellation":
                        return <Badge bg="secondary">Completed</Badge>;
                    default:
                        return <Badge bg="primary">Not Started</Badge>;
                }
            },
        },
        {
            name: "Assigned Staff",
            selector: (row) => row.staff_name,
            sortable: true,
        },

        {
            name: "Cancellation Privilege",
            selector: (row) => row.cancelFlag,
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-3">
                    {/* Status badge */}
                    {row.cancelFlag === 1 ? (
                        <Badge bg="success">Enabled</Badge>
                    ) : (
                        <Badge bg="danger">Disabled</Badge>
                    )}

                    <span
                        role="button"
                        onClick={() =>
                            handleUpdateCustomer(row.customer_id, {
                                cancelFlag: row.cancelFlag === 1 ? 0 : 1,
                            })
                        }
                        style={{ cursor: "pointer", fontSize: "1.5rem" }}
                    >
                        {row.cancelFlag === 1 ? (
                            <FiToggleRight className="text-success" />
                        ) : (
                            <FiToggleLeft className="text-danger" />
                        )}
                    </span>
                </div>
            ),
        },
    ];

    const filteredCustomers = customers.filter((c) =>
        c.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                {/* Header Controls */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                    <div className="input-group" style={{ maxWidth: "300px" }}>
                        <span className="input-group-text">
                            <FiSearch />
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={fetchCustomers}
                        >
                            <FiRefreshCw /> Refresh
                        </Button>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredCustomers}
                        pagination
                        highlightOnHover
                        responsive
                        striped
                        persistTableHead
                        fixedHeader
                        fixedHeaderScrollHeight="600px"
                        defaultSortFieldId={1}
                        customStyles={{
                            headCells: {
                                style: {
                                    backgroundColor: "#f8f9fa",
                                    fontWeight: "bold",
                                },
                            },
                        }}
                    />
                )}
            </div>
            <ToastContainer position="top-right" autoClose={1200} newestOnTop />
        </div>
    );
};

export default Access;
